import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { eventsApi } from '@/lib/api';
import { Plus, Edit, Trash2, Calendar, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const buildScheduleText = (formData) => {
  const freq = formData.recurrence_frequency;
  const time = formData.event_time || '';
  const timeStr = time ? ` at ${time}` : '';

  if (freq === 'daily') return `Every day${timeStr}`;
  if (freq === 'weekly' && formData.recurrence_day !== null && formData.recurrence_day !== '') {
    return `Every ${DAYS_OF_WEEK[formData.recurrence_day]}${timeStr}`;
  }
  if (freq === 'biweekly' && formData.recurrence_day !== null && formData.recurrence_day !== '') {
    return `Every other ${DAYS_OF_WEEK[formData.recurrence_day]}${timeStr}`;
  }
  if (freq === 'monthly' && formData.recurrence_date) {
    const d = formData.recurrence_date;
    const suffix = d === 1 || d === 21 || d === 31 ? 'st' : d === 2 || d === 22 ? 'nd' : d === 3 || d === 23 ? 'rd' : 'th';
    return `${d}${suffix} of every month${timeStr}`;
  }
  if (freq === 'yearly') {
    return `Annually${timeStr}`;
  }
  return '';
};

const AdminEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const emptyForm = {
    title: '',
    description: '',
    date: '',
    end_date: '',
    location: "St. John's UCC",
    is_recurring: false,
    recurrence_frequency: '',
    recurrence_day: '',
    recurrence_date: '',
    event_time: '',
  };

  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const data = await eventsApi.getAll(false);
      setEvents(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description || '',
      date: item.date?.slice(0, 16) || '',
      end_date: item.end_date?.slice(0, 16) || '',
      location: item.location || "St. John's UCC",
      is_recurring: item.is_recurring || false,
      recurrence_frequency: item.recurrence_frequency || '',
      recurrence_day: item.recurrence_day !== null && item.recurrence_day !== undefined ? String(item.recurrence_day) : '',
      recurrence_date: item.recurrence_date !== null && item.recurrence_date !== undefined ? String(item.recurrence_date) : '',
      event_time: item.event_time || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.date) {
      toast.error('Please fill required fields');
      return;
    }

    try {
      const scheduleText = formData.is_recurring ? buildScheduleText(formData) : '';
      const submitData = {
        title: formData.title,
        description: formData.description,
        date: formData.date ? new Date(formData.date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        location: formData.location,
        is_recurring: formData.is_recurring,
        recurrence_frequency: formData.is_recurring ? formData.recurrence_frequency : null,
        recurrence_day: formData.is_recurring && (formData.recurrence_frequency === 'weekly' || formData.recurrence_frequency === 'biweekly') ? parseInt(formData.recurrence_day) : null,
        recurrence_date: formData.is_recurring && formData.recurrence_frequency === 'monthly' ? parseInt(formData.recurrence_date) : null,
        recurrence_pattern: scheduleText || null,
        event_time: formData.event_time || null,
      };

      if (editingItem) {
        await eventsApi.update(editingItem.id, submitData);
        toast.success('Event updated');
      } else {
        await eventsApi.create(submitData);
        toast.success('Event created');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Event save error:', error);
      toast.error(error.message || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await eventsApi.delete(id);
      toast.success('Deleted');
      loadData();
    } catch (error) {
      toast.error('Failed');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Events</h1>
          <p className="text-stone-600">Manage church events and regular activities</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-amber-700 hover:bg-amber-800" data-testid="add-event-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Event
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Event' : 'Add Event'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required data-testid="event-title-input" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date/Time *</Label>
                  <Input type="datetime-local" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required data-testid="event-date-input" />
                </div>
                <div>
                  <Label>End Date/Time</Label>
                  <Input type="datetime-local" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Location</Label>
                <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} />
              </div>

              {/* Recurring Event Toggle */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base">Recurring Event</Label>
                    <p className="text-xs text-stone-500">Recurring events appear in "Regular Church Activities"</p>
                  </div>
                  <Switch checked={formData.is_recurring} onCheckedChange={(checked) => setFormData({ ...formData, is_recurring: checked })} data-testid="recurring-toggle" />
                </div>
              </div>

              {formData.is_recurring && (
                <div className="space-y-4 bg-amber-50 p-4 rounded-lg border border-amber-200">
                  <div>
                    <Label>Frequency</Label>
                    <Select value={formData.recurrence_frequency} onValueChange={(val) => setFormData({ ...formData, recurrence_frequency: val, recurrence_day: '', recurrence_date: '' })}>
                      <SelectTrigger data-testid="frequency-select">
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        {FREQUENCIES.map(f => (
                          <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {(formData.recurrence_frequency === 'weekly' || formData.recurrence_frequency === 'biweekly') && (
                    <div>
                      <Label>Day of Week</Label>
                      <Select value={formData.recurrence_day} onValueChange={(val) => setFormData({ ...formData, recurrence_day: val })}>
                        <SelectTrigger data-testid="day-select">
                          <SelectValue placeholder="Select day" />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS_OF_WEEK.map((day, i) => (
                            <SelectItem key={i} value={String(i)}>{day}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {formData.recurrence_frequency === 'monthly' && (
                    <div>
                      <Label>Day of Month</Label>
                      <Select value={formData.recurrence_date} onValueChange={(val) => setFormData({ ...formData, recurrence_date: val })}>
                        <SelectTrigger data-testid="date-select">
                          <SelectValue placeholder="Select date" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                            <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <Label>Time (for schedule display)</Label>
                    <Input
                      type="time"
                      value={formData.event_time}
                      onChange={(e) => setFormData({ ...formData, event_time: e.target.value })}
                      data-testid="event-time-input"
                    />
                  </div>

                  {formData.recurrence_frequency && (
                    <div className="text-sm text-amber-800 font-medium">
                      Schedule: {buildScheduleText(formData) || 'Complete the fields above'}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
                <Button type="submit" className="bg-amber-700 hover:bg-amber-800" data-testid="event-save-btn">{editingItem ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <Card><CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700 mx-auto"></div>
        </CardContent></Card>
      ) : events.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <div className="font-medium">{item.title}</div>
                    <div className="text-sm text-stone-500 line-clamp-1">{item.description}</div>
                  </TableCell>
                  <TableCell className="text-sm">{formatDate(item.date)}</TableCell>
                  <TableCell>{item.location}</TableCell>
                  <TableCell>
                    {item.is_recurring ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                        <RefreshCw className="h-3 w-3" /> {item.recurrence_pattern || 'Recurring'}
                      </span>
                    ) : (
                      <span className="text-sm text-stone-500">One-time</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(item)} data-testid={`edit-event-${item.id}`}><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(item.id)} data-testid={`delete-event-${item.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card><CardContent className="p-8 text-center">
          <Calendar className="h-12 w-12 text-stone-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-stone-800 mb-2">No Events</h3>
          <Button onClick={() => setDialogOpen(true)} className="bg-amber-700 hover:bg-amber-800"><Plus className="h-4 w-4 mr-2" /> Add Event</Button>
        </CardContent></Card>
      )}
    </div>
  );
};

export default AdminEvents;
